import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, setUser } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const googleBtnRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav(loc.state?.from || "/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  const handleGoogleCredential = useCallback(async (response) => {
    try {
      const { data } = await api.post("/auth/google", { id_token: response.credential });
      localStorage.setItem("fe_token", data.token);
      setUser(data.user);
      toast.success("Welcome");
      nav(loc.state?.from || "/dashboard", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Google sign-in failed");
    }
  }, [nav, loc, setUser]);

  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId || !window.google || !googleBtnRef.current) return;
    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: handleGoogleCredential,
    });
    window.google.accounts.id.renderButton(googleBtnRef.current, {
      theme: "outline",
      size: "large",
      width: 360,
      text: "continue_with",
    });
  }, [handleGoogleCredential]);

  return (
    <div className="mx-auto max-w-md px-6 py-16">
      <div className="overline text-safety">Welcome back</div>
      <h1 className="mt-2 font-display font-black text-4xl tracking-tighter text-forest">Log in</h1>
      <form onSubmit={submit} className="mt-8 border border-border bg-white p-8 space-y-5">
        <div>
          <div className="overline text-muted-foreground mb-2">Email</div>
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" className="rounded-none h-12" />
        </div>
        <div>
          <div className="overline text-muted-foreground mb-2">Password</div>
          <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" className="rounded-none h-12" />
        </div>
        <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full rounded-none bg-forest hover:bg-forest-800 text-white h-12">
          {loading ? "Signing in\u2026" : "Log in"}
        </Button>
        <div className="relative py-2">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
          <div className="relative flex justify-center text-xs overline text-muted-foreground bg-white px-3 mx-auto w-fit">or</div>
        </div>
        <div ref={googleBtnRef} className="flex justify-center" data-testid="login-google" />
      </form>
      <div className="mt-6 text-sm text-muted-foreground text-center">
        No account?{" "}
        <Link to="/register" className="text-forest hover:text-safety" data-testid="login-to-register">Create one</Link>
        <div className="mt-2">
          <Link to="/forgot-password" className="text-forest hover:text-safety" data-testid="login-forgot">Forgot password?</Link>
        </div>
      </div>
    </div>
  );
}
